// Component: TradeHistory.jsx
// Purpose: Unified trade history — groups orders into trade lifecycles

import React, { useMemo, useContext } from 'react';
import TradeCard from './TradeCard';
import { groupOrdersIntoTrades, resolvePositionForEntry, buildOpenPositionFallbackTrade } from '../../utils/tradingTransforms';
import { TradingDataContext } from '../../contexts/TradingDataContext';

export default function TradeHistory({ orders, positions, mode = 'full', maxTrades = 5 }) {
  //--> Access ltpRef directly from context (useTradingData doesn't expose it)
  const ctx = useContext(TradingDataContext);
  const ltpRef = ctx?.ltpRef;

  //--> Group flat orders into trade lifecycles
  const trades = useMemo(() => groupOrdersIntoTrades(orders, positions), [orders, positions]);

  //--> trades-view-strategy-backfill (issue #3) — groupOrdersIntoTrades needs an
  //*   ENTRY order present in `orders` to synthesize ANY trade group. If the
  //*   entry order is missing (order-window starvation — see
  //*   useStrategyOrdersBackfill, TradingDataContext.jsx:892-920), trades comes
  //*   back [] even though the strategy has a live OMS position. Rather than
  //*   fall through to "No trades yet", surface one fallback card per open
  //*   position built straight from the OMS snapshot. Only engages when the
  //*   entry-anchored path found NOTHING — a real (even partial) trade list
  //*   always wins.
  const openPositionFallbackTrades = useMemo(() => {
    if (trades.length > 0) return [];
    return (positions || [])
      .filter((p) => p.status === 'OPEN' || parseFloat(p.qty ?? p.quantity ?? 0) > 0)
      .map((p) => buildOpenPositionFallbackTrade(p))
      .filter(Boolean);
  }, [trades, positions]);

  const displayTrades = trades.length > 0 ? trades : openPositionFallbackTrades;

  //--> In compact mode, limit visible trades
  const visibleTrades = mode === 'compact' ? displayTrades.slice(0, maxTrades) : displayTrades;

  //--> Find current position for open trade SL/TP levels (D10 — instrument-scoped + leg-preferring
  //    via resolvePositionForEntry, NOT a bare-sii .find() that would collapse MANUAL multi-instrument
  //    positions or pick an OMS-vs-BD row by array order).
  const openTrade = displayTrades.find((t) => t.status === 'open');
  const currentPosition = openTrade
    ? (openTrade.isPositionOnly
        //--> A position-only fallback card has no entryOrder to resolve
        //*   against — it WAS built directly from a position row (carried on
        //*   _position), so reuse that row instead of calling
        //*   resolvePositionForEntry(positions, null).
        ? openTrade._position ?? null
        : resolvePositionForEntry(positions, openTrade.entryOrder))
    : null;

  //--> Get LTP for open trade instrument from ltpRef
  //*   A position-only fallback card has no entryOrder — fall back to the
  //*   carried position row's instrument_id (see buildOpenPositionFallbackTrade).
  const openLtp = openTrade && ltpRef?.current
    ? ltpRef.current.get(openTrade.entryOrder?.instrument_id ?? openTrade._position?.instrument_id) ?? null
    : null;

  if (!visibleTrades.length) {
    return (
      <div style={{
        padding: '24px 16px', textAlign: 'center', color: 'var(--fg-3)', fontSize: 13,
      }}>
        No trades yet
      </div>
    );
  }

  return (
    <div style={{
      display: 'flex', flexDirection: 'column', gap: 12,
      ...(mode === 'full' ? { maxHeight: '70vh', overflowY: 'auto' } : {}),
    }}>
      {visibleTrades.map((trade) => (
        <TradeCard
          key={trade.id}
          trade={trade}
          ltp={trade.status === 'open' ? openLtp : null}
          position={trade.status === 'open' ? currentPosition : null}
          defaultExpanded={mode === 'full'}
        />
      ))}
    </div>
  );
}
